import React, { useEffect, useState } from "react"
import ReactApexChart from "react-apexcharts"
import moment from "moment"
import { DatePicker } from "antd"
const { RangePicker } = DatePicker

export default function ProfitLossChart({
  data,
  selectedUser,
  setSelectedUser,
  users,
}) {
  const [filteredInvestedData, setFilteredInvestedData] = useState([])
  const [filteredPortfolioData, setFilteredPortfolioData] = useState([])

  // RangePicker value (empty initially)
  const [dateRange, setDateRange] = useState([])

  useEffect(() => {
    // Convert object to array if needed
    const investedArray = data?.allInvestedAmountGraphValues
      ? Object.values(data.allInvestedAmountGraphValues)
      : []
    const portfolioArray = data?.allPortfolioGraphValues
      ? Object.values(data.allPortfolioGraphValues)
      : []

    // If user has selected a range, filter by it
    if (dateRange?.[0] && dateRange?.[1]) {
      const startDate = new Date(dateRange[0])
      const endDate = new Date(dateRange[1])
      endDate.setHours(23, 59, 59, 999) // include full day

      const filterByDate = arr =>
        (arr || []).filter(entry => {
          const createdAt = new Date(entry.createdAt) // plain Date
          return createdAt >= startDate && createdAt <= endDate
        })

      setFilteredInvestedData(filterByDate(investedArray))
      setFilteredPortfolioData(filterByDate(portfolioArray))
    } else {
      // No range selected → show last 7 days by default
      const last7Days = moment().subtract(7, "days").startOf("day")
      const todayEnd = moment().endOf("day")

      const filterByLast7Days = arr =>
        (arr || []).filter(entry => {
          const createdAt = moment(entry.createdAt)
          return createdAt.isBetween(last7Days, todayEnd, null, "[]")
        })

      setFilteredInvestedData(filterByLast7Days(investedArray))
      setFilteredPortfolioData(filterByLast7Days(portfolioArray))
    }
  }, [dateRange, data])

  const sortByDate = arr =>
    [...arr].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))

  const chartDataPortfolio = sortByDate(filteredInvestedData)
    .filter(
      entry =>
        entry.totalInvestedAmount != null && !isNaN(entry.totalInvestedAmount)
    )
    .map(entry => ({
      x: new Date(entry.createdAt),
      y: parseFloat(entry.totalInvestedAmount),
    }))

  const chartDataFinalPortfolio = sortByDate(filteredPortfolioData).map(
    (entry, index, arr) => {
      let adjustedValue = parseFloat(entry.finalPortfolioValue)

      // Subtract TradingPLAmount & ShorttermPLAmount from future points
      for (let i = index + 1; i < arr.length; i++) {
        const next = arr[i]
        adjustedValue -= parseFloat(next.TradingPLAmount || 0)
        adjustedValue -= parseFloat(next.ShorttermPLAmount || 0)
      }

      return {
        x: new Date(entry.createdAt),
        y: parseFloat(adjustedValue.toFixed(2)),
      }
    }
  )

  const minY = Math.min(
    ...chartDataPortfolio.map(d => d.y),
    ...chartDataFinalPortfolio.map(d => d.y)
  )
  const maxY = Math.max(
    ...chartDataPortfolio.map(d => d.y),
    ...chartDataFinalPortfolio.map(d => d.y)
  )

  const options = {
    chart: {
      type: "area",
      height: 350,
      zoom: { enabled: true },
      animations: { enabled: true, easing: "easeinout", speed: 800 },
    },
    dataLabels: { enabled: false },
    stroke: { width: 2, curve: "smooth" },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.1,
        stops: [0, 90, 100],
      },
    },
    markers: { size: 0 },
    xaxis: {
      type: "datetime",
      labels: {
        style: { colors: "#000", fontSize: "12px", fontWeight: "bold" },
      },
    },
    yaxis: {
      min: minY - 500,
      max: maxY + 500,
      labels: {
        style: { colors: "#000", fontSize: "12px", fontWeight: "bold" },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: val => `₹${val.toFixed(2)}` },
    },
    legend: {
      show: true,
      position: "top",
      labels: { colors: "#000", useSeriesColors: false },
    },
  }

  const series = [
    { name: "Initial Capital", data: chartDataPortfolio, color: "#2196F3" },
    {
      name: "Current Value",
      data: chartDataFinalPortfolio,
      color:
        chartDataFinalPortfolio[chartDataFinalPortfolio.length - 1]?.y >
        chartDataPortfolio[chartDataPortfolio.length - 1]?.y
          ? "#4CAF50"
          : "#F44336",
    },
  ]

  return (
    <div className="p-4 bg-white shadow rounded-lg">
      <div className="d-flex flex-column flex-xl-row justify-content-between mb-4 align-items-center">
        <h4 className="text-xl font-bold text-dark">Investment Value Graph</h4>
        <div className="d-flex flex-column flex-xl-row gap-3">
          <RangePicker
            onChange={dates => setDateRange(dates)}
            className="form-select1 rounded-5 py-2 text-dark"
            placement="bottom"
          />
          <select
            className="form-select1 px-3 bg-white w-auto rounded-5 text-black"
            value={selectedUser}
            onChange={e => setSelectedUser(e.target.value)}
          >
            {users?.map(item => (
              <option key={item?.id} value={item?.id}>
                {item?.customerName}
              </option>
            ))}
          </select>
        </div>
      </div>

      <ReactApexChart
        options={options}
        series={series}
        height={350}
        type="area"
      />
    </div>
  )
}
